/**
 * 日次/月次ダッシュボードの集計(現行シート「日次集計」「スタッフ別」「原価率」の再現)。
 * 元データは daily_summary(§9)。ここは純粋関数のみ(Firestore に触らない)。
 *
 * 現行シートとの対応:
 *  - 取得率 = 取得数 ÷ 通話回数 / 1取得あたり通話時間 = 通話時間 ÷ 取得数 / 見込取得数(アポ/h) = 取得数 ÷ BB稼働(受付+通話+後処理。出勤の実稼働ではない)
 *  - 稼働合計 = 受付 + 通話 + 後処理(休憩・離席は含まない)
 *  - 原価率 = 報酬 ÷ 売上。売上 = 取得数 × 割振会社の単価(実単価。割振が無い行は加重平均単価で換算)
 *  - 管理者なし合計 = 品川+在宅時給+歩合 / 管理者あり合計 = それに管理者(給湯器)を加える
 *    (仕様書 §5.3: 管理者の取得件数は売上に含め、管理者の報酬は管理者込み原価率の分子にのみ入る)
 *  - 週番号 W1〜W6 = 月曜始まり・月内通番(現行シートと同じ)
 *  - スタッフ別成績は 親=アポインター(人) ／ 子=割振会社(BB ID×会社) の階層(③指示)
 */
import {
  BUSINESS_LABELS,
  HOUR_BANDS,
  priceOn,
  styleLabel,
  type BusinessType,
  type Company,
  type DailySummary,
  type DateStr,
  type Delivery,
  type DeliveryStyle,
  type MonthTargets,
  type StaffCategory,
  type StaffDaily,
  type Yen,
} from '@core/lib/types';

// ---- 種別・単価 -------------------------------------------------------------

export function categoryOf(s: Pick<StaffDaily, 'work_style' | 'pay_type' | 'business'>): StaffCategory {
  if (s.work_style === 'kanri') return 'kanrisha';
  if (s.business === 'fuyohin') return 'white';
  if (s.work_style === 'shinagawa' || s.work_style === 'timee') return 'shinagawa';
  return s.pay_type === 'commission' ? 'buai' : 'zaitaku_jikyu';
}

/** 「品川/時給」表記(出勤形態×報酬区分)。架電内容(給湯器/不用品)は別の列に出す */
export function styleOf(s: Pick<StaffDaily, 'work_style' | 'pay_type'>): string {
  return styleLabel(s.work_style, s.pay_type);
}

/**
 * 売上単価: 会社マスタの単価(改訂履歴からその日の単価を引く)を優先する。
 * マスタで単価を改訂すれば、適用開始日以降の日次・月次に反映される。
 * マスタに無い会社は取込時のスナップショット(unit_prices)、それも無ければ加重平均単価
 */
export function unitPriceFor(s: StaffDaily, summary: DailySummary, targets: MonthTargets, companies?: Map<string, Company>): Yen {
  if (s.company_id) {
    const c = companies?.get(s.company_id);
    if (c) return priceOn(c, summary.date);
    const p = summary.unit_prices[s.company_id];
    if (p !== undefined) return p;
  }
  return targets.fallback_unit_price[s.business];
}

// ---- RANK(見込取得数/h からの帯。不用品は1/10スケール) -------------------

export type Rank = 'S' | 'A' | 'B+' | 'B-' | 'C' | '—';

export function rankOf(apph: number | null, business: BusinessType): Rank {
  if (apph === null) return '—';
  const v = business === 'fuyohin' ? apph * 10 : apph;
  if (v >= 5) return 'S';
  if (v >= 3.5) return 'A';
  if (v >= 2.5) return 'B+';
  if (v >= 2.0) return 'B-';
  return 'C';
}

// ---- スタッフ1行(BB ID×割振会社)の指標 --------------------------------------

export type StaffMetrics = StaffDaily & {
  date: DateStr;
  category: StaffCategory;
  is_manager: boolean;
  unit_price: Yen;
  revenue: Yen;
  work_sec: number; // 稼働合計(受付+通話+後処理)
  /** 実稼働(h)。丸める前の値。出勤=拘束−休憩(0.5h単位) / 在宅・管理者=稼働合計。通話/h の分母 */
  real_hours: number;
  apph: number | null; // 1時間あたりの見込取得数
  hit_rate: number | null; // 取得率(%)
  talk_per_appt: number | null; // 1取得あたりの通話時間(秒)
  work_per_appt: number | null; // 1取得あたりの稼働時間(秒)
  calls_per_hour: number | null;
  rank: Rank;
  cost_rate: number | null; // %
};

export function staffMetrics(s: StaffDaily, summary: DailySummary, targets: MonthTargets, companies?: Map<string, Company>): StaffMetrics {
  const unitPrice = unitPriceFor(s, summary, targets, companies);
  const revenue = s.appointments * unitPrice;
  const workSec = s.reception_sec + s.talk_sec + s.after_sec;
  // 見込/h は現行シートどおり BB稼働(受付+通話+後処理)で割る(bluebean-import.md §0-3)。
  // 出勤の実稼働(拘束−休憩・D-56)は RANK の判定には使わない
  const bbHours = workSec / 3600;
  const apph = bbHours > 0 ? s.appointments / bbHours : null;
  // 通話/h は 通話回数 ÷ **実稼働**(現行シート。2026-09-12 指摘で修正)。
  // 在宅・管理者の実稼働は稼働合計そのものだが、worked_hours は保存時に小数2桁へ丸めてあるため、
  // 丸める前の値で割る(丸めた値で割ると 1 ずれることがある)
  const realHours = s.hours_source === 'auto' || s.hours_source === 'manual' ? s.worked_hours : bbHours;
  return {
    ...s,
    date: summary.date,
    category: categoryOf(s),
    is_manager: s.work_style === 'kanri',
    unit_price: unitPrice,
    revenue,
    work_sec: workSec,
    real_hours: realHours,
    apph,
    hit_rate: s.calls > 0 ? (s.appointments / s.calls) * 100 : null,
    talk_per_appt: s.appointments > 0 ? s.talk_sec / s.appointments : null,
    work_per_appt: s.appointments > 0 ? workSec / s.appointments : null,
    calls_per_hour: realHours > 0 ? s.calls / realHours : null,
    rank: rankOf(apph, s.business),
    cost_rate: revenue > 0 ? (s.pay / revenue) * 100 : null,
  };
}

/**
 * 納品内訳(品川／歩合／時給)のどれに当たるか。現行PL日報の分け方:
 *  予約アポ数=品川・タイミー・管理者(出勤) / 在宅歩合 / 在宅時給(不用品含む)
 */
export function deliveryStyleOf(r: Pick<StaffDaily, 'work_style' | 'pay_type'>): DeliveryStyle {
  if (r.work_style === 'zaitaku') return r.pay_type === 'commission' ? 'buai' : 'jikyu';
  return 'shinagawa';
}

// ---- 集計 --------------------------------------------------------------------

export type Aggregate = {
  appointments: number;
  hours: number;
  /** 実稼働(h)の合計。丸める前の値。通話/h の分母 */
  real_hours: number;
  pay: Yen;
  revenue: Yen;
  commute: Yen;
  calls: number;
  reception_sec: number;
  /** 受付回数。受付AVG の分母(D-124)。受付回数を持たない古い行は数えない */
  reception_count: number;
  talk_sec: number;
  after_sec: number;
  /** 後処理回数(無い日は通話回数で代用) */
  after_count: number;
  count: number; // 行数(BB ID×会社×日)
  by_hour: number[];
};

export function emptyAgg(): Aggregate {
  return {
    appointments: 0,
    hours: 0,
    real_hours: 0,
    pay: 0,
    revenue: 0,
    commute: 0,
    calls: 0,
    reception_sec: 0,
    reception_count: 0,
    talk_sec: 0,
    after_sec: 0,
    after_count: 0,
    count: 0,
    by_hour: HOUR_BANDS.map(() => 0),
  };
}

export function addAgg(agg: Aggregate, m: StaffMetrics): Aggregate {
  return {
    appointments: agg.appointments + m.appointments,
    hours: agg.hours + m.worked_hours,
    real_hours: agg.real_hours + m.real_hours,
    pay: agg.pay + m.pay,
    revenue: agg.revenue + m.revenue,
    commute: agg.commute + m.commute,
    calls: agg.calls + m.calls,
    reception_sec: agg.reception_sec + m.reception_sec,
    reception_count: agg.reception_count + (m.reception_count ?? 0),
    talk_sec: agg.talk_sec + m.talk_sec,
    after_sec: agg.after_sec + m.after_sec,
    after_count: agg.after_count + (m.after_count && m.after_count > 0 ? m.after_count : m.calls),
    count: agg.count + 1,
    by_hour: agg.by_hour.map((v, i) => v + (m.by_hour[i] ?? 0)),
  };
}

export function sumAgg(rows: StaffMetrics[]): Aggregate {
  return rows.reduce(addAgg, emptyAgg());
}

export function costRate(agg: Aggregate): number | null {
  return agg.revenue > 0 ? (agg.pay / agg.revenue) * 100 : null;
}

/** BB稼働(受付+通話+後処理)の時間。見込/h・通話/h の分母(agg.hours は実稼働の合計で、人件費用) */
export function bbHoursOf(agg: Pick<Aggregate, 'reception_sec' | 'talk_sec' | 'after_sec'>): number {
  return (agg.reception_sec + agg.talk_sec + agg.after_sec) / 3600;
}

export function apphOf(agg: Aggregate): number | null {
  const h = bbHoursOf(agg);
  return h > 0 ? agg.appointments / h : null;
}

/** 集計から日次集計と同じ派生指標を出す(親=アポインター行などに使う) */
export function derived(agg: Aggregate) {
  const workSec = agg.reception_sec + agg.talk_sec + agg.after_sec;
  return {
    work_sec: workSec,
    apph: apphOf(agg),
    hit_rate: agg.calls > 0 ? (agg.appointments / agg.calls) * 100 : null,
    talk_per_appt: agg.appointments > 0 ? agg.talk_sec / agg.appointments : null,
    work_per_appt: agg.appointments > 0 ? workSec / agg.appointments : null,
    calls_per_hour: agg.real_hours > 0 ? agg.calls / agg.real_hours : null,
    /** 1回あたりの受付時間(秒)= 受付時間 ÷ 受付回数(2026-09-22 追加。D-124) */
    reception_avg: agg.reception_count > 0 ? agg.reception_sec / agg.reception_count : null,
    /** 1通話あたりの通話時間(秒)。現行シートの「通話時間 AVG.」 */
    talk_avg: agg.calls > 0 ? agg.talk_sec / agg.calls : null,
    /** 1回あたりの後処理時間(秒)。現行シートの「後処理時間 AVG.」 */
    after_avg: agg.after_count > 0 ? agg.after_sec / agg.after_count : null,
    cost_rate: costRate(agg),
  };
}

/** 親=アポインター(人)／子=割振会社(BB ID×会社)の階層(③指示) */
export type AppointerGroup = {
  op_id: string;
  name: string;
  /** 出勤形態×報酬区分(複数あれば「／」区切り) */
  style: string;
  /** 架電内容(給湯器／不用品。複数あれば「／」区切り) */
  businesses: string;
  business: BusinessType;
  is_manager: boolean;
  agg: Aggregate;
  rank: Rank;
  children: StaffMetrics[];
};

export function groupByAppointer(rows: StaffMetrics[]): AppointerGroup[] {
  const map = new Map<string, StaffMetrics[]>();
  for (const r of rows) map.set(r.op_id, [...(map.get(r.op_id) ?? []), r]);
  return [...map.values()]
    .map((list) => {
      const first = list[0]!;
      const agg = sumAgg(list);
      const styles = Array.from(new Set(list.map(styleOf)));
      const businesses = Array.from(new Set(list.map((r) => BUSINESS_LABELS[r.business])));
      const business: BusinessType = list.every((r) => r.business === 'fuyohin') ? 'fuyohin' : 'kyutoki';
      return {
        op_id: first.op_id,
        name: first.name,
        style: styles.join('／'),
        businesses: businesses.join('／'),
        business,
        is_manager: first.is_manager,
        agg,
        rank: rankOf(apphOf(agg), business),
        children: [...list].sort((a, b) => b.appointments - a.appointments),
      };
    })
    .sort((a, b) => (apphOf(b.agg) ?? -1) - (apphOf(a.agg) ?? -1));
}

/** 会社別の階層(④指示): 親=会社 ／ 子=出勤形態(品川/時給 など) ／ 孫=アポインター */
export type CompanyTree = {
  company_id: string | null; // null = 割振なし
  name: string;
  unit_price: Yen | null;
  agg: Aggregate;
  styles: { key: string; label: string; agg: Aggregate; appointers: { op_id: string; name: string; agg: Aggregate }[] }[];
};

/**
 * 「納品件数の入力」で手修正した取得件数(⑬指示)。キー = `${date}|${company_id}|${品川/歩合/時給}`。
 * 売上見込・PL のアポ数・会社別の取得件数は、この値がある日はこちらを使う(日次集計の値は BlueBean の生値のまま残す)
 */
export function apptOverrideMap(deliveries: Delivery[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const d of deliveries) {
    for (const [cid, det] of Object.entries(d.details ?? {})) {
      for (const [style, v] of Object.entries(det.appointments ?? {})) if (v !== undefined) m.set(`${d.date}|${cid}|${style}`, v);
    }
  }
  return m;
}

/** 行(同じ会社)の取得件数を、手修正のある日はその値に置き換えて合計する */
function adjustedAppointments(list: StaffMetrics[], overrides: Map<string, number>, cid: string, style: DeliveryStyle): { appointments: number; adjusted: boolean } {
  let total = 0;
  let adjusted = false;
  const seen = new Set<string>();
  for (const r of list) {
    const key = `${r.date}|${cid}|${style}`;
    const o = overrides.get(key);
    if (o !== undefined) {
      adjusted = true;
      if (!seen.has(key)) {
        seen.add(key);
        total += o;
      }
      continue;
    }
    total += r.appointments;
  }
  return { appointments: total, adjusted };
}

export function groupByCompany(rows: StaffMetrics[], companies: Company[], date: DateStr, deliveries: Delivery[] = []): CompanyTree[] {
  const byCompany = new Map<string, StaffMetrics[]>();
  for (const r of rows) byCompany.set(r.company_id ?? '', [...(byCompany.get(r.company_id ?? '') ?? []), r]);
  const overrides = apptOverrideMap(deliveries);
  const build = (id: string | null, name: string, unitPrice: Yen | null, list: StaffMetrics[]): CompanyTree => {
    const byStyle = new Map<string, StaffMetrics[]>();
    for (const r of list) {
      const key = styleOf(r);
      byStyle.set(key, [...(byStyle.get(key) ?? []), r]);
    }
    const styles = [...byStyle.entries()]
      .map(([key, l]) => {
        const byOp = new Map<string, StaffMetrics[]>();
        for (const r of l) byOp.set(r.op_id, [...(byOp.get(r.op_id) ?? []), r]);
        const appointers = [...byOp.values()]
          .map((ol) => ({ op_id: ol[0]!.op_id, name: ol[0]!.name, agg: sumAgg(ol) }))
          .sort((a, b) => b.agg.appointments - a.agg.appointments);
        let agg = sumAgg(l);
        // 手修正した取得件数(会社×出勤形態×日)があれば、出勤形態の行と売上見込に反映(アポインター別は BlueBean の値のまま)
        if (id && overrides.size > 0) {
          const first = l[0]!;
          const adj = adjustedAppointments(l, overrides, id, deliveryStyleOf(first));
          if (adj.adjusted) agg = { ...agg, appointments: adj.appointments, revenue: adj.appointments * (unitPrice ?? first.unit_price) };
        }
        return { key, label: key, agg, appointers };
      })
      .sort((a, b) => b.agg.appointments - a.agg.appointments);
    let agg = sumAgg(list);
    if (styles.some((s) => s.agg.appointments !== sumAgg(byStyle.get(s.key) ?? []).appointments)) {
      agg = { ...agg, appointments: styles.reduce((s, x) => s + x.agg.appointments, 0), revenue: styles.reduce((s, x) => s + x.agg.revenue, 0) };
    }
    return { company_id: id, name, unit_price: unitPrice, agg, styles };
  };
  // 会社マスタの全社を出す(取得0の会社も並べる)。割振なしの行があれば最後に付ける
  const trees = companies.map((c) => build(c.id, c.name, priceOn(c, date), byCompany.get(c.id) ?? []));
  const unassigned = byCompany.get('');
  if (unassigned && unassigned.length > 0) trees.push(build(null, '割振なし', null, unassigned));
  return trees.sort((a, b) => {
    if (a.company_id === null) return 1;
    if (b.company_id === null) return -1;
    return b.agg.revenue - a.agg.revenue;
  });
}

/** 粗利 = 売上見込み − 報酬 / 粗利率 = 粗利 ÷ 売上見込み */
export function grossOf(agg: Aggregate): { gross: Yen; rate: number | null } {
  const gross = agg.revenue - agg.pay;
  return { gross, rate: agg.revenue > 0 ? (gross / agg.revenue) * 100 : null };
}

/** 現行シート「スタッフ別」「原価率」の種別行 */
export type CategoryKey =
  | 'shinagawa'
  | 'zaitaku_jikyu'
  | 'buai'
  | 'no_manager'
  | 'with_manager'
  | 'white_ex_manager'
  | 'white'
  | 'total';

export const CATEGORY_ROW_LABELS: Record<CategoryKey, string> = {
  shinagawa: '出勤/時給',
  zaitaku_jikyu: '在宅/時給',
  buai: '在宅/歩合',
  no_manager: '管理者なし合計',
  with_manager: '管理者あり合計',
  white_ex_manager: '不用品(管抜)',
  white: '不用品',
  total: '総合計',
};

export const CATEGORY_KEYS = Object.keys(CATEGORY_ROW_LABELS) as CategoryKey[];

export function categoryRows(rows: StaffMetrics[]): Record<CategoryKey, Aggregate> {
  const pick = (f: (m: StaffMetrics) => boolean) => sumAgg(rows.filter(f));
  const kyutokiStaff = (m: StaffMetrics) => !m.is_manager && m.business === 'kyutoki';
  return {
    shinagawa: pick((m) => m.category === 'shinagawa'),
    zaitaku_jikyu: pick((m) => m.category === 'zaitaku_jikyu'),
    buai: pick((m) => m.category === 'buai'),
    no_manager: pick(kyutokiStaff),
    with_manager: pick((m) => m.business === 'kyutoki'),
    white_ex_manager: pick((m) => !m.is_manager && m.business === 'fuyohin'),
    white: pick((m) => m.business === 'fuyohin'),
    total: sumAgg(rows),
  };
}

/**
 * 種別行に対応する目標原価率(%)。**不用品の行は不用品の目標、それ以外は給湯器の目標**
 * (2026-09-24 発注者指示「PL・会社別・出勤形態別の赤も同じ基準にそろえる」)
 */
export function targetFor(key: CategoryKey, targets: MonthTargets): number | null {
  return key === 'white' || key === 'white_ex_manager' ? targets.cost_rate_fuyohin : targets.cost_rate_total;
}

// ---- 日付・週 ------------------------------------------------------------------

/** 月曜始まり・月内通番の週番号(W1〜W6)。現行シートと同じ数え方 */
export function weekOfMonth(date: DateStr): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  let week = 1;
  for (let day = 2; day <= d; day++) {
    if (new Date(Date.UTC(y, m - 1, day)).getUTCDay() === 1) week++;
  }
  return week;
}

export function daysInMonth(month: string): number {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

export function dateOf(month: string, day: number): DateStr {
  return `${month}-${String(day).padStart(2, '0')}`;
}

export function addDays(date: DateStr, days: number): DateStr {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

export function addMonths(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const dt = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`;
}

// ---- 表示 ----------------------------------------------------------------------

export function fmtDuration(sec: number | null): string {
  if (sec === null) return '—';
  const s = Math.round(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

export function fmtPct(v: number | null, digits = 2): string {
  return v === null || !Number.isFinite(v) ? '—' : `${v.toFixed(digits)}%`;
}

export function fmtNum(v: number | null, digits = 2): string {
  return v === null || !Number.isFinite(v) ? '—' : v.toFixed(digits);
}

/** 目標との比較色: 目標以下=良(緑) / 超過=赤 */
export function rateClass(v: number | null, target: number | null): string {
  if (v === null || target === null) return '';
  return v <= target ? 'pos' : 'neg';
}
